import type { WorkspaceInformationResponse } from "@/features/workspaces/types/workspaceInformation.ts";

import { useNavigate, useParams } from "react-router-dom";
import { useDebouncedCallback } from "use-debounce";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS } from "@/constants/api.ts";
import useWorkspaceInformationStore from "@/features/workspaces/store/useWorkspaceInformationStore.tsx";
import GenericLoader from "@/components/GenericLoader.tsx";
import PATHS from "@/routes/paths";

interface Props {
  children: React.ReactNode;
}

export default function WorkspaceWrapper({ children }: Props) {
  const { workspaceId } = useParams();
  const { t } = useTranslation("workspaces");
  const redirect = useNavigate();

  const { setData, setMembers, data: workspaceData } = useWorkspaceInformationStore();
  const [isLoading, setIsLoading] = useState(true);

  const getBasicInfo = async () => {
    try {
      setIsLoading(true);
      const {
        data: { data },
      } = await axiosApiInstance<WorkspaceInformationResponse>(
        `${API_ENDPOINTS.WORKSPACES.WORKSPACES}/${workspaceId}/basics`,
      );

      setData(data);
      setMembers(data.members);
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const debouncedGetBasicInfo = useDebouncedCallback(getBasicInfo, 300);

  useEffect(() => {
    if (workspaceId === "main" && workspaceData) {
      setData(undefined);
      setIsLoading(false);
    } else if (workspaceId === "main") {
      setIsLoading(false);
    } else if (workspaceId !== undefined && workspaceId !== workspaceData?.id) {
      debouncedGetBasicInfo();
    } else if (workspaceId === undefined) {
      setIsLoading(false);
      setData(undefined);
    } else {
      setIsLoading(false);
    }
  }, [workspaceId]);

  if (isLoading) {
    return <GenericLoader className={"fixed top-0 left-0 z-40"} />;
  }
  if (location.pathname === PATHS.TUTOR.WORKSPACES.MAIN) {
    redirect(PATHS.TUTOR.OVERVIEW);
  } else if (location.pathname === PATHS.STUDENT.WORKSPACES.MAIN) {
    redirect(PATHS.STUDENT.OVERVIEW);
  }

  if (workspaceId === "main") {
    return (
      <>
        <title>{`${t("mainWorkspace")} - Ximly`}</title>
        {children}
      </>
    );
  }

  if (workspaceId === undefined) {
    return (
      <>
        <title>{`${t("workspacesList", "Workspaces")} - Ximly`}</title>
        {children}
      </>
    );
  }

  if (!isLoading) {
    return (
      <>
        <title>{`${workspaceData ? workspaceData.name : t("classroom")} - Ximly`}</title>
        {children}
      </>
    );
  }
}
